import { Text } from '@radix-ui/themes';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  trpcConsumerGetCheckoutContactOtpPromptStatus,
  trpcConsumerSendCheckoutContactOtp,
  trpcConsumerVerifyCheckoutContactOtp,
} from '@trashlab/api/src/generated/hooks';

type CheckoutContactVerificationMethod = 'email' | 'phone';
type CheckoutContactPromptStatus = 'candidate' | 'none' | 'verified';

type CheckoutContactPromptMethodStatus = {
  status: CheckoutContactPromptStatus;
  valueKey: string | null;
};

type CheckoutContactPromptStatusData = {
  email: CheckoutContactPromptMethodStatus;
  phone: CheckoutContactPromptMethodStatus;
  preferredMethod: CheckoutContactVerificationMethod | null;
};

type ActivePrompt = {
  method: CheckoutContactVerificationMethod;
  valueKey: string;
};

type CheckoutContactVerificationProps = {
  cartId: string;
  tenantId: string;
  emailValue: string | null | undefined;
  phoneValue: string | null | undefined;
  savedContactRevision: number;
  onBeforeSend?: () => Promise<void>;
  onVerified?: () => void | Promise<void>;
};

const PROMPT_METHODS = ['phone', 'email'] as const;

function normalizeContactValueForKey(
  method: CheckoutContactVerificationMethod,
  value: string | null | undefined,
) {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return null;

  if (method === 'email') {
    return trimmed.toLowerCase();
  }

  let parsed = parsePhoneNumberFromString(trimmed);
  if (!parsed?.isValid()) {
    parsed = parsePhoneNumberFromString(trimmed, 'US');
  }

  if (parsed?.isValid()) {
    return parsed.format('E.164');
  }

  return trimmed;
}

function getPromptKey(prompt: ActivePrompt) {
  return `${prompt.method}:${encodeURIComponent(prompt.valueKey)}`;
}

function parsePromptKey(key: string): ActivePrompt | null {
  const separatorIndex = key.indexOf(':');
  if (separatorIndex === -1) {
    return null;
  }

  const methodKey = key.slice(0, separatorIndex);
  if (methodKey !== 'email' && methodKey !== 'phone') {
    return null;
  }

  return {
    method: methodKey,
    valueKey: decodeURIComponent(key.slice(separatorIndex + 1)),
  };
}

function isMethodCurrent(
  method: CheckoutContactVerificationMethod,
  status: CheckoutContactPromptMethodStatus | undefined,
  localValueKeys: Record<CheckoutContactVerificationMethod, string | null>,
) {
  return Boolean(status?.valueKey) && status?.valueKey === localValueKeys[method];
}

function getActivePrompt(
  status: CheckoutContactPromptStatusData | undefined,
  skippedPromptKeys: string[],
  localValueKeys: Record<CheckoutContactVerificationMethod, string | null>,
): ActivePrompt | null {
  if (!status) {
    return null;
  }

  for (const method of PROMPT_METHODS) {
    const methodStatus = status[method];
    if (
      methodStatus.status !== 'candidate' ||
      !methodStatus.valueKey ||
      !isMethodCurrent(method, methodStatus, localValueKeys)
    ) {
      continue;
    }

    const prompt = {
      method,
      valueKey: methodStatus.valueKey,
    };

    if (!skippedPromptKeys.includes(getPromptKey(prompt))) {
      return prompt;
    }
  }

  return null;
}

function getVerifiedMethods(
  status: CheckoutContactPromptStatusData | undefined,
  localValueKeys: Record<CheckoutContactVerificationMethod, string | null>,
) {
  if (!status) {
    return [];
  }

  return PROMPT_METHODS.filter((method) => {
    const methodStatus = status[method];
    return (
      methodStatus.status === 'verified' &&
      isMethodCurrent(method, methodStatus, localValueKeys)
    );
  });
}

function getMethodLabel(method: CheckoutContactVerificationMethod) {
  return method === 'email' ? 'email' : 'phone';
}

export function CheckoutContactVerification({
  cartId,
  tenantId,
  emailValue,
  phoneValue,
  savedContactRevision,
  onBeforeSend,
  onVerified,
}: CheckoutContactVerificationProps) {
  const promptStatusQuery =
    trpcConsumerGetCheckoutContactOtpPromptStatus.useQuery(
      { cartId, tenantId },
      {
        refetchOnWindowFocus: false,
      },
    );
  const sendOtp = trpcConsumerSendCheckoutContactOtp.useMutation();
  const verifyOtp = trpcConsumerVerifyCheckoutContactOtp.useMutation();
  const localValueKeys = useMemo(
    () => ({
      email: normalizeContactValueForKey('email', emailValue),
      phone: normalizeContactValueForKey('phone', phoneValue),
    }),
    [emailValue, phoneValue],
  );
  const [skippedPromptKeys, setSkippedPromptKeys] = useState<string[]>([]);
  const [otpId, setOtpId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const previousLocalValueKeysRef = useRef(localValueKeys);
  const lastSavedContactRevisionRef = useRef(savedContactRevision);

  useEffect(() => {
    const previous = previousLocalValueKeysRef.current;
    previousLocalValueKeysRef.current = localValueKeys;

    setSkippedPromptKeys((current) =>
      current.filter((key) => {
        const parsedKey = parsePromptKey(key);
        if (!parsedKey) {
          return false;
        }

        const { method, valueKey } = parsedKey;
        if (previous[method] !== localValueKeys[method]) {
          return valueKey !== previous[method];
        }

        return true;
      }),
    );
  }, [localValueKeys]);

  useEffect(() => {
    if (lastSavedContactRevisionRef.current === savedContactRevision) {
      return;
    }

    lastSavedContactRevisionRef.current = savedContactRevision;
    void promptStatusQuery.refetch();
  }, [promptStatusQuery, savedContactRevision]);

  const activePrompt = getActivePrompt(
    promptStatusQuery.data,
    skippedPromptKeys,
    localValueKeys,
  );
  const activePromptKey = activePrompt ? getPromptKey(activePrompt) : null;
  const verifiedMethods = getVerifiedMethods(
    promptStatusQuery.data,
    localValueKeys,
  );

  useEffect(() => {
    setOtpId(null);
    setCode('');
    setError(null);
  }, [activePromptKey]);

  const isLoading = sendOtp.isLoading || verifyOtp.isLoading;

  const handleSkip = () => {
    if (!activePrompt) {
      return;
    }

    setSkippedPromptKeys((current) => [
      ...current.filter((key) => key !== getPromptKey(activePrompt)),
      getPromptKey(activePrompt),
    ]);
    setOtpId(null);
    setCode('');
    setError(null);
  };

  const handleSend = async () => {
    setError(null);

    try {
      await onBeforeSend?.();
      const refreshedStatus = await promptStatusQuery.refetch();
      const currentPrompt = getActivePrompt(
        refreshedStatus.data,
        skippedPromptKeys,
        localValueKeys,
      );

      if (!currentPrompt) {
        setError('Could not send code');
        return;
      }

      const result = await sendOtp.mutateAsync({
        cartId,
        tenantId,
        method: currentPrompt.method,
        value: currentPrompt.valueKey,
      });
      setOtpId(result.otpId);
    } catch {
      setError('Could not send code');
    }
  };

  const handleVerify = async () => {
    setError(null);
    if (!otpId || !activePrompt) return;

    try {
      await verifyOtp.mutateAsync({
        cartId,
        tenantId,
        method: activePrompt.method,
        value: activePrompt.valueKey,
        otpId,
        code,
      });
      setOtpId(null);
      setCode('');
      await promptStatusQuery.refetch();
      await onVerified?.();
    } catch {
      setError('Invalid code');
    }
  };

  if (!activePrompt && verifiedMethods.length === 0) {
    return null;
  }

  return (
    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
      {verifiedMethods.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {verifiedMethods.map((method) => (
            <span
              key={method}
              className="rounded border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
            >
              {getMethodLabel(method)} verified
            </span>
          ))}
        </div>
      )}

      {activePrompt && (
        <div className="flex flex-col gap-2">
          <Text size="2">
            We found an existing account for this{' '}
            {getMethodLabel(activePrompt.method)}. Verify to reuse it, or skip
            and we'll create a new account.
          </Text>

          {!otpId ? (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={isLoading}
                onClick={handleSend}
                className="h-[34px] rounded-md border border-[var(--accent-7)] bg-[var(--accent-3)] px-3 text-sm font-medium text-[var(--accent-11)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Sending' : 'Send Code'}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="h-[34px] rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700"
              >
                Skip
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="Verification code"
                value={code}
                onChange={(event) => setCode(event.currentTarget.value)}
                maxLength={6}
                placeholder="Code"
                className="h-[34px] w-[90px] rounded-md border border-gray-300 bg-white px-2 text-sm text-black focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                type="button"
                disabled={code.length !== 6 || isLoading}
                onClick={handleVerify}
                className="h-[34px] rounded-md border border-[var(--accent-7)] bg-[var(--accent-3)] px-3 text-sm font-medium text-[var(--accent-11)] disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isLoading ? 'Checking' : 'Confirm'}
              </button>
              <button
                type="button"
                onClick={handleSkip}
                className="h-[34px] rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700"
              >
                Skip
              </button>
            </div>
          )}

          {error && (
            <Text size="1" color="red">
              {error}
            </Text>
          )}
        </div>
      )}
    </div>
  );
}
